import { PourConfig, CalculationResult, Language } from '../types';
import { formatNumber, formatCurrency } from '../utils/calculations';
import { LANGUAGE_NAMES } from '../utils/translations';

// Core function to call the private Gemini API endpoint
async function askGemini(userPrompt: string, systemRole: string = "You are a helpful assistant"): Promise<string> {
  try {
    const response = await fetch("https://lightapp-api.promptvault.work", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        prompt: userPrompt, 
        systemInstruction: systemRole 
      })
    });
    
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    
    const data = await response.json();
    
    if (data.error) throw new Error(data.error);
    return data.answer; 
  } catch (error) {
    console.error("Gemini API Request Failed:", error);
    return "The system is currently busy. Please try again in a moment. (Error: " + (error instanceof Error ? error.message : String(error)) + ")";
  }
}

export const getExpertAnalysis = async (config: PourConfig, result: CalculationResult, language: Language): Promise<string> => {
  const shapeStr = config.shape === 'rectangle' 
    ? `Rectangular mold (${config.dimensions.length} x ${config.dimensions.width} x ${config.dimensions.depth} ${config.unitSystem === 'imperial' ? 'in' : 'cm'})`
    : `Circular mold (${config.dimensions.diameter} dia x ${config.dimensions.depth} depth ${config.unitSystem === 'imperial' ? 'in' : 'cm'})`;

  const costContext = result.estimatedCost > 0 
    ? `- Estimated Material Cost: ${formatCurrency(result.estimatedCost)} (based on user input)`
    : `- Cost: Not calculated`;

  const languageName = LANGUAGE_NAMES[language];

  // We keep the prompt in English for the model's consistent reasoning, but instruct it to output in the target language.
  const prompt = `
    I am planning an epoxy resin pour project. Here are the calculated specifications:
    
    - Project Geometry: ${shapeStr}
    - Total Resin Volume Needed: ${formatNumber(result.totalVolume)} ${result.unit}
    - Mixing Ratio (by volume): ${config.ratioA}:${config.ratioB} (Part A : Part B)
    ${costContext}
    
    As a professional Resin Artist, please analyze this data.
    1. **Volume Check**: Is this considered a deep pour? Do I need a specific "Deep Pour" resin to prevent overheating?
    2. **Cost/Value**: Brief comment on the project scale/cost.
    3. **Safety**: 2-3 critical safety tips for this specific volume.
    4. **Environment**: Recommended temperature for curing.
    
    IMPORTANT: Provide the response in ${languageName}.
    Format the output with clear headings or bullet points. Keep it encouraging but realistic.
  `;

  const systemInstruction = `You are an expert Resin Artist and Chemical Safety Consultant. Your goal is to help users create beautiful art safely and cost-effectively. You MUST answer in ${languageName}.`;

  return await askGemini(prompt, systemInstruction);
};

export const chatWithExpert = async (message: string, context: string | undefined, language: Language): Promise<string> => {
  const languageName = LANGUAGE_NAMES[language];
  
  const prompt = context 
    ? `Context about current project: ${context}\n\nUser Question: ${message}\n\nPlease answer in ${languageName}.`
    : `${message}\n\nPlease answer in ${languageName}.`;

  const systemInstruction = `You are a friendly, expert resin artist. Answer clearly and briefly. If asked about brands, suggest looking for 'non-yellowing' or 'food safe' certifications rather than promoting specific companies. You MUST answer in ${languageName}.`;

  return await askGemini(prompt, systemInstruction);
};